import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';

interface FloatingCartProps {
  onOpenCart: () => void;
}

export default function FloatingCart({ onOpenCart }: FloatingCartProps) {
  const { cart, showCartModal, showDetailModal } = useApp();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  // Mostrar/ocultar el carrito flotante basado en si hay elementos Y si los modales no están abiertos
  useEffect(() => {
    if (cart.length > 0 && !showCartModal && !showDetailModal) {
      setIsVisible(true);
      // Animación de entrada
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    } else {
      setIsVisible(false);
    }
  }, [cart.length, showCartModal, showDetailModal]);

  // Animación cuando se agrega un elemento
  useEffect(() => {
    if (cart.length > 0 && !showCartModal && !showDetailModal) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [getTotalItems(), showCartModal, showDetailModal]);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50">
      <button
        onClick={onOpenCart}
        className={`
          relative bg-gradient-to-br from-[#FFF3C4] to-[#FDE7A9] text-[#2C4358] rounded-2xl px-5 py-4 
          shadow-[0_10px_25px_rgba(32,178,170,0.15)] hover:shadow-[0_12px_30px_rgba(32,178,170,0.25)]
          transition-all duration-300 transform hover:scale-105 active:scale-95
          ${isAnimating ? 'animate-bounce' : 'breathing'}
          flex items-center space-x-3 min-w-[190px] max-w-[240px]
          border border-[#F1E4B6] hover:border-[#EAD894] ring-1 ring-[#20B2AA]/10 hover:ring-[#20B2AA]/30
        `}
      >
        {/* Glow de fondo suave */}
        <div className="pointer-events-none absolute -inset-3 -z-10 rounded-3xl bg-[#20B2AA]/20 blur-2xl opacity-60 animate-pulse" />

        {/* Icono del carrito */}
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-inner border border-[#E8E0D0]">
            <span className="text-xl text-gray-600">🛒</span>
          </div>
          {/* Badge con cantidad */}
          {getTotalItems() > 0 && (
            <div className="absolute -top-1 -right-1 bg-[#ff6b6b] shadow-md text-white text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {getTotalItems() > 99 ? '99+' : getTotalItems()}
            </div>
          )}
        </div>

        {/* Información del carrito */}
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-medium text-[#2C4358] truncate">
            {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
          </div>
          <div className="text-lg font-extrabold text-[#20B2AA] truncate drop-shadow-sm">
            {formatPrice(getTotalAmount())}
          </div>
        </div>

        {/* Flecha indicadora */}
        <div className="text-white flex-shrink-0">
          <span className="text-lg bg-[#20B2AA] w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md">→</span>
        </div>

        {/* Shimmer suave */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
          <div className="absolute -inset-1 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
        </div>
      </button>

      {/* Efecto de ondas cuando se agrega un elemento */}
      {isAnimating && (
        <div className="absolute inset-0 rounded-2xl bg-[#20B2AA] opacity-20 animate-ping" />
      )}
      <style>{`
        @keyframes breath {
          0%, 100% { transform: scale(1); box-shadow: 0 10px 25px rgba(32,178,170,0.15); }
          50% { transform: scale(1.03); box-shadow: 0 12px 30px rgba(32,178,170,0.28); }
        }
        .breathing { animation: breath 2.6s ease-in-out infinite; }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer { animation: shimmer 2.5s infinite; }
      `}</style>
    </div>
  );
} 